import { AttachFile, TextFields } from "@mui/icons-material";
import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import type { FindAllDeliveryOptionsQuery } from "@/libs/generated/operations";
import dateFormat from "@/utils/date";
import { formatEuro } from "@/utils/delivery-pricing";
import { RowActions } from "./components/RowActions";

type OriginalDeliveryOptionRecord = FindAllDeliveryOptionsQuery["findAllDeliveryOptions"][number];

export type DeliveryOptionRecord = Omit<OriginalDeliveryOptionRecord, "requiresPhoto"> & {
  requiresFile: boolean;
  fileType: "IMAGE" | "PDF" | null;
};

export function getDeliveryOptionsColumns(
  t: TFunction,
  callbacks: {
    onEdit: (record: DeliveryOptionRecord) => void;
  }
): ColumnDef<DeliveryOptionRecord>[] {
  return [
    {
      accessorKey: "id",
      meta: { label: t("common.table.headers.ID") },
      enableHiding: false,
      header: ({ column }) => <DataTableColumnHeader column={column} title={"ID"} />,
      cell: ({ row }) => <Typography variant="body2">{row.original.id}</Typography>,
      enableSorting: true,
    },
    {
      accessorKey: "code",
      meta: { label: t("common.table.headers.Code") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Code"} />,
      cell: ({ row }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {row.original.code}
        </Typography>
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      accessorKey: "name",
      meta: { label: t("common.table.headers.Name") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Name"} />,
      cell: ({ row }) => (
        <Stack>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.original.name}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {row.original.partnerAssignments?.length ?? 0}{" "}
            {t("deliveryOption.table.partnerAssignments")}
          </Typography>
        </Stack>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "defaultClientPrice",
      meta: { label: t("common.table.headers.Default price") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Default price"} />,
      cell: ({ row }) => (
        <Typography variant="body2">{formatEuro(row.original.defaultClientPrice)}</Typography>
      ),
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: "visibilityMode",
      meta: { label: t("common.table.headers.Visibility") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Visibility"} />,
      cell: ({ row }) => {
        const label =
          row.original.visibilityMode === "GLOBAL"
            ? t("deliveryOption.table.visibility.global")
            : t("deliveryOption.table.visibility.partner");
        return <Chip label={label} size="small" variant="outlined" />;
      },
      enableSorting: true,
    },
    {
      id: "flags",
      header: () => (
        <Typography variant="body2" fontWeight={600}>
          {t("common.table.headers.Input required")}
        </Typography>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          {row.original.requiresFile && (
            <Tooltip title={`File required (${row.original.fileType})`}>
              <AttachFile fontSize="small" color="action" />
            </Tooltip>
          )}
          {row.original.requireInput && (
            <Tooltip title={t("common.table.headers.Input required")}>
              <TextFields fontSize="small" color="action" />
            </Tooltip>
          )}
          {!row.original.requiresFile && !row.original.requireInput && (
            <Typography variant="caption" color="text.disabled">
              &mdash;
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      accessorKey: "active",
      meta: { label: t("common.table.headers.Active") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Active"} />,
      cell: ({ row }) => (
        <Chip
          color={row.original.active ? "success" : "default"}
          label={row.original.active ? t("common.shared.yes") : t("common.shared.no")}
          size="small"
          variant="outlined"
        />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "sortOrder",
      meta: { label: t("common.table.headers.Sort order") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Sort order"} />,
      cell: ({ row }) => <Typography variant="body2">{row.original.sortOrder}</Typography>,
      enableSorting: true,
    },
    {
      accessorKey: "createdAt",
      meta: { label: t("common.table.headers.Created at") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Created at"} />,
      cell: ({ row }) => (
        <Typography variant="body2">{dateFormat(row.original.createdAt)}</Typography>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "updatedAt",
      meta: { label: t("common.table.headers.Updated at") },
      header: ({ column }) => <DataTableColumnHeader column={column} title={"Updated at"} />,
      cell: ({ row }) => (
        <Typography variant="body2">{dateFormat(row.original.updatedAt)}</Typography>
      ),
      enableSorting: true,
    },
    {
      id: "actions",
      enableHiding: false,
      enableSorting: false,
      size: 80,
      header: () => (
        <Typography variant="body2" fontWeight={600}>
          {t("common.table.headers.action")}
        </Typography>
      ),
      cell: ({ row }) => <RowActions record={row.original} onEdit={callbacks.onEdit} />,
    },
  ];
}
